'use client';

import { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { trackAddToCart, trackRemoveFromCart } from '@/lib/analytics/ga4';
import { fbTrackAddToCart } from '@/lib/analytics/fbpixel';

export interface CartItem {
  cart_item_id: number;
  cart_id: number;
  product_id: number;
  category_id?: number;
  fabric_id?: number;
  quantity: number;
  width?: number;
  height?: number;
  color_id?: number;
  material_id?: number;
  unit_price: number;
  // UI fields
  name?: string;
  slug?: string;
  colorName?: string;
  materialName?: string;
  mountType?: number;
  mountTypeName?: string;
  controlType?: string;
  headrailId?: number;
  headrailName?: string;
  bottomRailId?: number;
  bottomRailName?: string;
  image?: string;
  totalPrice?: number;
  // Room and configuration fields
  roomType?: string;
  configuration?: any;
  // Allow any additional fields for configurator data
  [key: string]: any;
}

interface VendorDiscount {
  type: 'vendor_discount' | 'vendor_coupon';
  vendor_id: number;
  vendor_name: string;
  discount_id?: number;
  coupon_id?: number;
  coupon_code?: string;
  name: string;
  discount_type: string;
  amount: number;
  applied_to: string;
  vendor_subtotal?: number;
  vendor_subtotal_after?: number;
}

interface PricingDetails {
  subtotal: number;
  vendor_discounts: VendorDiscount[];
  vendor_coupons: VendorDiscount[];
  total_discount_amount: number;
  applied_discounts_list: VendorDiscount[];
  // $10 sample credit applied to this cart (null if none / not eligible).
  sample_credit?: { code: string; amount: number } | null;
  shipping: number;
  shipping_coupon?: {
    code: string;
    name: string;
  } | null;
  tax: number;
  tax_rate: number;
  tax_breakdown?: {
    state_tax: number;
    county_tax: number;
    city_tax: number;
    special_district_tax: number;
  };
  tax_jurisdiction?: string;
  zip_code?: string;
  total: number;
  vendors_in_cart: number;
  applied_promotions?: {
    coupon_code?: string;
    discount_type?: string;
    discount_amount?: number;
  };
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => Promise<void>;
  removeItem: (cart_item_id: number) => Promise<void>;
  updateQuantity: (cart_item_id: number, quantity: number) => Promise<void>;
  updateItem: (cart_item_id: number, item: CartItem) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  itemCount: number;
  subtotal: number;
  pricing: PricingDetails;
  applyCoupon: (code: string) => Promise<{ success: boolean; message?: string; notice?: string }>;
  removeCoupon: () => void;
  updateZipCode: (zipCode: string) => void;
  updatePricingWithTax: (newPricing: PricingDetails) => void;
  isLoading: boolean;
  pricingError: string | null;
}

const defaultPricing: PricingDetails = {
  subtotal: 0,
  vendor_discounts: [],
  vendor_coupons: [],
  total_discount_amount: 0,
  applied_discounts_list: [],
  sample_credit: null,
  shipping: 0,
  shipping_coupon: null,
  tax: 0,
  tax_rate: 0,
  total: 0,
  vendors_in_cart: 0,
  applied_promotions: {}
};

const CartContext = createContext<CartContextType>({
  items: [],
  addItem: async () => {},
  removeItem: async () => {},
  updateQuantity: async () => {},
  updateItem: async () => {},
  clearCart: async () => {},
  refreshCart: async () => {},
  itemCount: 0,
  subtotal: 0,
  pricing: defaultPricing,
  applyCoupon: async () => ({ success: false }),
  removeCoupon: () => {},
  updateZipCode: () => {},
  updatePricingWithTax: () => {},
  isLoading: false,
  pricingError: null,
});

export const useCart = () => useContext(CartContext);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [pricing, setPricing] = useState<PricingDetails>(defaultPricing);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<{ id?: number; type?: string; email?: string } | null>(null);
  const [zipCode, setZipCode] = useState<string>('78701'); // Default to Austin, TX

  const refreshCart = useCallback(async () => {
    const response = await fetch('/api/v2/commerce/cart');
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error('Unable to load your cart. Please refresh the page.');
    setItems((result.data || result).items || []);
  }, []);

  // Helper function to check if user is authenticated and is a customer
  const isAuthenticated = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/v2/auth/me');
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;// Only allow customers to use the cart
        if (data.user?.role === 'customer') {
          setCustomerData({
            id: data.user?.user_id,
            type: 'retail',
            email: data.user?.email,
          });
          return true;
        }
        // For non-customers, clear customer data and return false
        setCustomerData(null);
        return false;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Calculate pricing using the pricing API
  const calculatePricing = async (cartItems: CartItem[], couponCode?: string | null) => {
    if (cartItems.length === 0) {
      setPricing(defaultPricing);
      return;
    }

    try {
      setIsLoading(true);
      setPricingError(null);

      const pricingRequest = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          base_price: item.unit_price
        })),
        customer_id: customerData?.id,
        customer_type: customerData?.type,
        // Logged-in customer email — lets the server surface their own $10
        // sample credit on the cart page. Guests see it at checkout once they
        // enter their email there.
        email: customerData?.email,
        // An explicit null means "no code" (removeCoupon): don't fall back to
        // appliedCoupon, whose value in this closure is still the old code.
        coupon_code: couponCode === null ? undefined : (couponCode || appliedCoupon),
        // Flag a fabric-sample order so the server applies the flat $10
        // shipping & handling. Guest carts live only in localStorage (not the
        // DB), so the server can't otherwise detect the sample order.
        has_sample_order: cartItems.some(
          (i) => (i.configuration as any)?.type === 'sample_order',
        ),
        // Note: tax calculation only happens at checkout with ZIP code
      };

      // Create cache key based on request content
      // Calculate pricing without cache

      const response = await fetch('/api/v2/commerce/cart/calculate-pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pricingRequest)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to calculate pricing');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to calculate pricing');
      }
      const data = result.data;
      
      const pricingData = {
        subtotal: data.subtotal || 0,
        vendor_discounts: data.vendor_discounts || [],
        vendor_coupons: data.vendor_coupons || [],
        total_discount_amount: data.total_discount_amount || 0,
        applied_discounts_list: data.applied_discounts_list || [],
        sample_credit: data.sample_credit || null,
        shipping: data.shipping || 0,
        tax: data.tax || 0,
        tax_rate: data.tax_rate || 0,
        tax_breakdown: data.tax_breakdown,
        tax_jurisdiction: data.tax_jurisdiction,
        zip_code: data.zip_code,
        total: data.total || 0,
        vendors_in_cart: data.vendors_in_cart || 0,
        applied_promotions: data.applied_promotions || {}
      };

      // Pricing data calculated successfully

      setPricing(pricingData);

      if (data.coupon_error) {
        setPricingError(data.coupon_error);
        setAppliedCoupon(null);
      }

    } catch (error) {
      setPricingError(error instanceof Error ? error.message : 'Failed to calculate pricing');
      // No fallback - pricing calculation must succeed through the API
      setPricing(defaultPricing);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Load cart from API or localStorage on mount
    const loadCart = async () => {
      setIsLoading(true);
      try {
        // Refresh customerData (logged-in customer) as a side effect. The cart
        // itself is always server-backed now — guests are identified by the
        // sessionId cookie, customers by their auth token — so we read it from
        // the API for everyone instead of localStorage.
        await isAuthenticated();

        await refreshCart();
        // One-time migration: if an old localStorage guest cart still exists,
        // push its items into the server cart, then drop it.
        try {
          const legacy = localStorage.getItem('guest_cart');
          if (legacy) {
            const parsed = JSON.parse(legacy);
            const legacyItems = Array.isArray(parsed) ? parsed : (parsed.items || []);
            for (const li of legacyItems) {
              const productId = li.productId || li.product_id;
              const vendorId = li.vendorId || li.vendor_id || li.configuration?.vendorId;
              if (!productId || !vendorId) continue;
              await fetch('/api/v2/commerce/cart/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: parseInt(productId), vendorId: parseInt(vendorId), quantity: li.quantity || 1, configuration: { name: li.name || '', unit_price: li.unit_price || 0, ...(li.configuration || {}) } }),
              }).catch(() => {});
            }
            localStorage.removeItem('guest_cart');
            const reload = await fetch('/api/v2/commerce/cart');
            if (reload.ok) { const r = await reload.json(); if (r.success) setItems((r.data || r).items || []); }
          }
        } catch (e) { /* ignore legacy migration errors */ }
      } catch (error) {
        // Error loading cart
        console.error('Error loading cart:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCart();
    
    // Listen for storage events to reload cart after login
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_changed') {
        loadCart();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check for login success in URL
    if (window.location.search.includes('login=success')) {
      setTimeout(loadCart, 1000); // Delay to ensure auth cookie is set
    }
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Recalculate pricing when items or ZIP code changes
  useEffect(() => {
    calculatePricing(items);
  }, [items, customerData, zipCode]);

  // Add an item to cart
  const addItem = async (newItem: CartItem) => {
    try {
      // Check user role - only customers and guests can add to cart
      // No role gate. Staff (admin / vendor / installer / dealer) can use the
      // cart exactly like a customer — one storefront for every role, the
      // a large online retailer model. This used to fetch /api/v2/auth/me and alert()
      // "Only customers can add items to cart", which was the REAL enforcement:
      // the configurator's disabled button was only the visible half, so simply
      // un-disabling it would still have failed on click.
      // Staff-placed web orders are attributed via orders.sales_staff_id in
      // CommerceHandler.createOrder rather than being blocked.
      // Cart is server-backed for everyone now (guests via the sessionId
      // cookie). Keep isAuthenticated() for its customerData side effect.
      await isAuthenticated();
      {
        // Extract only the fields the API expects
        const productId = newItem.productId || newItem.product_id;
        const vendorId = newItem.vendorId || newItem.vendor_id;
        
        // Validate required fields
        if (!productId || !vendorId) {
          console.error('Missing required fields:', { productId, vendorId, newItem });
          throw new Error('Unable to add item to cart. Product information is incomplete.');
        }
        
        const apiData = {
          productId: parseInt(productId),
          vendorId: parseInt(vendorId),
          quantity: newItem.quantity || 1,
          configuration: {
            // Include display fields that cart page needs
            name: newItem.name || '',
            slug: newItem.slug || '',
            image: newItem.image || '',
            unit_price: newItem.unit_price || 0,
            // Configuration details
            width: newItem.width?.toString() || '0',
            height: newItem.height?.toString() || '0',
            widthFraction: newItem.widthFraction?.toString() || '0',
            heightFraction: newItem.heightFraction?.toString() || '0',
            roomType: newItem.roomType || '',
            mountType: newItem.mountType || '',
            fabricType: newItem.fabricType || '',
            fabricName: newItem.fabricName || '',
            colorOption: newItem.colorOption || '',
            controlOption: newItem.controlOption || newItem.controlType || '',
            valanceOption: newItem.valanceOption || '',
            bottomRailOption: newItem.bottomRailOption || '',
            // Include any other fields from the original configuration
            ...(newItem.configuration || {})
          }
        };
        
        const response = await fetch('/api/v2/commerce/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(apiData)
        });
        const result = await response.json();
        if (response.ok && result.success) {
            // GA4 + Meta Pixel: Track add to cart. Each in its own try so a
            // blocked or broken tag can never stop the cart refresh below.
            try { trackAddToCart(newItem, newItem.quantity || 1); } catch { /* tracking is optional */ }
            try { fbTrackAddToCart({ id: newItem.product_id, name: newItem.product_name || '', category: newItem.category_name, price: newItem.unit_price, quantity: newItem.quantity || 1 }); } catch { /* tracking is optional */ }
            // After successfully adding item, reload the entire cart
            await refreshCart().catch((error) => console.error('Item saved; cart refresh failed:', error));
        } else {
          // API error adding item
          let errorMessage = 'Failed to add item to cart';
          try {
            const errorData = result;
            console.error('Error adding to cart:', {
              status: response.status,
              statusText: response.statusText,
              errorData,
              sentData: apiData
            });
            errorMessage = typeof errorData.error === 'string' ? errorData.error : errorData.message || errorData.error?.message || errorMessage;
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Remove an item from cart
  const removeItem = async (cart_item_id: number) => {
    try {
      // GA4: Track remove from cart before removing
      const removedItem = items.find(item => item.cart_item_id === cart_item_id);
      if (removedItem) trackRemoveFromCart(removedItem);

      // Check user role - only customers and guests can modify cart
      // No role gate — see the note in addItem.
      // If response is not ok, user is not authenticated (guest user) - allow them to proceed
      
      await isAuthenticated();
      {
        const response = await fetch(`/api/v2/commerce/cart/items/${cart_item_id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          // After successful deletion, fetch the updated cart
          const cartResponse = await fetch('/api/v2/commerce/cart');
          if (cartResponse.ok) {
            const cartResult = await cartResponse.json();
            if (cartResult.success) {
              const cartData = cartResult.data || cartResult;
              setItems(cartData.items || []);
              // Recalculate pricing
              await calculatePricing(cartData.items || [], appliedCoupon);
            }
          }
        }
      }
    } catch (error) {
      // Error removing item from cart
    }
  };

  // Update item quantity
  const updateQuantity = async (cart_item_id: number, quantity: number) => {
    try {
      if (quantity <= 0) {
        return; // Don't remove item, just prevent zero quantities
      }

      // Check user role - only customers and guests can modify cart
      // No role gate — see the note in addItem.
      // If response is not ok, user is not authenticated (guest user) - allow them to proceed

      await isAuthenticated();
      {
        const response = await fetch(`/api/v2/commerce/cart/items/${cart_item_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity })
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const data = result.data || result;
            setItems(data.items || []);
          }
        }
      }
    } catch (error) {
      // Error updating cart item
    }
  };

  // Update an entire cart item (for editing)
  const updateItem = async (cart_item_id: number, newItem: CartItem) => {
    try {
      // Check user role - only customers and guests can modify cart
      // No role gate — see the note in addItem.
      await isAuthenticated();
      {
        // Extract configuration from newItem
        const configuration = {
          // Include display fields that cart page needs
          name: newItem.name || '',
          slug: newItem.slug || '',
          image: newItem.image || '',
          unit_price: newItem.unit_price || 0,
          // Configuration details
          roomType: newItem.roomType || '',
          mountType: newItem.mountType || '',
          width: newItem.width?.toString() || '0',
          height: newItem.height?.toString() || '0',
          widthFraction: newItem.widthFraction?.toString() || '0',
          heightFraction: newItem.heightFraction?.toString() || '0',
          fabricType: newItem.fabricType || '',
          fabricName: newItem.fabricName || '',
          colorOption: newItem.colorOption || '',
          controlOption: newItem.controlOption || newItem.controlType || '',
          valanceOption: newItem.valanceOption || '',
          bottomRailOption: newItem.bottomRailOption || '',
          // Include any other fields from the original configuration
          ...(newItem.configuration || {})
        };
        
        // Only include quantity if explicitly provided (server will preserve existing if not sent)
        const requestBody: { configuration: typeof configuration; quantity?: number } = {
          configuration
        };

        // Only send quantity if it's a valid positive number
        if (newItem.quantity && newItem.quantity > 0) {
          requestBody.quantity = newItem.quantity;
        }

        const response = await fetch(`/api/v2/commerce/cart/items/${cart_item_id}/full`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        
        const result = await response.json();
        if (response.ok && result.success) {
            const cartData = result.data || result;
            setItems(cartData.items || []);
        } else {
          throw new Error(typeof result.error === 'string' ? result.error : result.message || 'Failed to update cart item. Please try again.');
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      // Check user role - only customers and guests can modify cart
      // No role gate — see the note in addItem.
      // If response is not ok, user is not authenticated (guest user) - allow them to proceed
      
      await isAuthenticated();
      {
        const response = await fetch('/api/v2/commerce/cart/clear', {
          method: 'POST'
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setItems([]);
            setAppliedCoupon(null);
          }
        }
      }
      try { localStorage.removeItem('guest_cart'); } catch { /* ignore */ }
    } catch (error) {
      // Error clearing cart
    }
  };

  // Apply coupon code
  const applyCoupon = async (code: string): Promise<{ success: boolean; message?: string; notice?: string }> => {
    if (!code) {
      return { success: false, message: 'Please enter a coupon code' };
    }

    try {
      setIsLoading(true);
      setPricingError(null);

      const pricingRequest = {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          base_price: item.unit_price
        })),
        customer_id: customerData?.id,
        customer_type: customerData?.type,
        coupon_code: code
      };

      const response = await fetch('/api/v2/commerce/cart/calculate-pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pricingRequest)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to apply coupon');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to apply coupon');
      }

      // Handle double-wrapped response from calculate-pricing endpoint
      const data = result.data?.data || result.data;

      // Update pricing state with new data
      const pricingData = {
        subtotal: data.subtotal || 0,
        vendor_discounts: data.vendor_discounts || [],
        vendor_coupons: data.vendor_coupons || [],
        total_discount_amount: data.total_discount_amount || 0,
        applied_discounts_list: data.applied_discounts_list || [],
        sample_credit: data.sample_credit || null,
        shipping: data.shipping || 0,
        tax: data.tax || 0,
        tax_rate: data.tax_rate || 0,
        tax_breakdown: data.tax_breakdown,
        tax_jurisdiction: data.tax_jurisdiction,
        zip_code: data.zip_code,
        total: data.total || 0,
        vendors_in_cart: data.vendors_in_cart || 0,
        applied_promotions: data.applied_promotions || {}
      };

      setPricing(pricingData);
      
      // Only set applied coupon if we actually have discounts
      if (data.vendor_coupons?.length > 0 || data.total_discount_amount > 0) {
        setAppliedCoupon(code);
      }
      
      // Check if coupon was successfully applied
      const hasCoupons = Array.isArray(data.vendor_coupons) && data.vendor_coupons.length > 0;
      const hasAppliedPromotion = data.applied_promotions?.coupon_code === code;

      if (hasCoupons || hasAppliedPromotion) {
        // coupon_notice: personal-window code held until checkout has the email.
        return data.coupon_notice
          ? { success: true, message: 'Coupon applied successfully', notice: data.coupon_notice }
          : { success: true, message: 'Coupon applied successfully' };
      } else {
        setAppliedCoupon(null);
        // coupon_error: personal-window code not valid for this customer.
        return { success: false, message: data.coupon_error || 'Coupon code not valid for items in cart' };
      }
      
    } catch (error) {
      setPricingError(error instanceof Error ? error.message : 'Failed to apply coupon');
      setAppliedCoupon(null);
      return { success: false, message: error instanceof Error ? error.message : 'Failed to apply coupon' };
    } finally {
      setIsLoading(false);
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    calculatePricing(items, null);
  };

  // Update ZIP code for tax calculation
  const updateZipCode = (newZipCode: string) => {
    setZipCode(newZipCode);
  };

  // Update pricing externally (used by checkout page for tax calculation)
  const updatePricingWithTax = (newPricing: PricingDetails) => {
    setPricing(newPricing);
  };

  // Calculate total number of items
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);

  // Calculate subtotal
  const subtotal = items.reduce((total, item) => total + (item.unit_price * item.quantity), 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        updateItem,
        clearCart,
        refreshCart,
        itemCount,
        subtotal,
        pricing,
        applyCoupon,
        removeCoupon,
        updateZipCode,
        updatePricingWithTax,
        isLoading,
        pricingError
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export default CartContext;
